import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, RefreshCw, Search } from 'lucide-react';
import { AiDetectionWarningPill } from '../../../../components/AiDetectionWarningPill';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import type { AiDetectionMetricRow } from '../../../../domain/aiDetectionMetrics';
import { formatCompactNumber } from '../TokenDashboardTool/aggregate';
import { useAiDetectionDashboardViewModel, useAiDetectionTableViewModel } from './viewModel';
import type { SortDirection, SortKey, TableSort } from './sort';

export function DashboardPanel() {
  const vm = useAiDetectionDashboardViewModel();
  useViewModelReady(vm.initialize);
  const { query, setQuery, visibleRows, sort, sortBy } = useAiDetectionTableViewModel(vm.report.rows);

  return (
    <div className="min-w-0">
      <p className="mt-4 max-w-3xl text-sm text-gray-500">
        All-time Pangram detection activity by expert. The warning pill marks the top 25% by
        accumulated warning flags among experts with at least one warning. Pills in the Users
        and campaign Experts views use a daily snapshot.
      </p>

      {vm.busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:min-w-[240px] sm:max-w-md sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, email, or expert ID…"
                aria-label="Search AI detection experts"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <button
              type="button"
              onClick={() => void vm.refresh()}
              disabled={vm.loading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {vm.loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
            </button>
          </div>

          {vm.error && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {vm.error}
            </div>
          )}

          <div className="relative mt-6">
            {vm.loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            <SummaryStrip report={vm.report} />
            <RankingTable rows={visibleRows} totalRows={vm.report.rows.length} sort={sort} sortBy={sortBy} />
          </div>
        </>
      )}
    </div>
  );
}

function SummaryStrip({ report }: { report: ReturnType<typeof useAiDetectionDashboardViewModel>['report'] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Metric label="Experts checked" value={report.rows.length.toLocaleString()} />
      <Metric label="Detection runs" value={formatCompactNumber(report.totals.detectionRuns)} />
      <Metric label="Info flags" value={formatCompactNumber(report.totals.infoFlags)} />
      <Metric
        label="Warning flags"
        value={formatCompactNumber(report.totals.warningFlags)}
        accent="text-amber-700"
      />
      <Metric
        label="Top warning quartile"
        value={`${report.topWarningExpertCount.toLocaleString()} / ${report.flaggedExpertCount.toLocaleString()}`}
        sub="experts with warnings"
        accent="text-amber-700"
      />
    </dl>
  );
}

export function Metric({
  label,
  value,
  sub,
  accent = 'text-gray-900',
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="min-w-0 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <dt className="text-[11px] text-gray-500">{label}</dt>
      <dd className={`mt-0.5 text-lg font-semibold leading-tight tabular-nums ${accent}`}>{value}</dd>
      {sub && <dd className="mt-0.5 text-[10px] text-gray-500">{sub}</dd>}
    </div>
  );
}

function RankingTable({ rows, totalRows, sort, sortBy }: {
  rows: AiDetectionMetricRow[];
  totalRows: number;
  sort: TableSort;
  sortBy: (key: SortKey) => void;
}) {
  const headerSort = (key: SortKey) => ({
    direction: sort.key === key ? sort.direction : undefined,
    onSort: () => sortBy(key),
  });
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Expert warning ranking</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {rows.length === totalRows ? `${totalRows.toLocaleString()} experts` : `${rows.length.toLocaleString()} of ${totalRows.toLocaleString()} experts`}
            {' · '}ties at the quartile cutoff are included
          </p>
        </div>
        <p className="text-[11px] text-gray-500">Warnings are field-level counts</p>
      </div>

      {rows.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          {totalRows === 0
            ? 'No AI detection data yet. Metrics appear after the next validation or submit.'
            : 'No experts match the current search.'}
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="AI detection expert warning ranking">
            <thead className="bg-gray-50">
              <tr>
                <Header label="#" {...headerSort('warningRank')} />
                <Header label="Expert" {...headerSort('name')} />
                <Header label="Runs" align="right" {...headerSort('detectionRuns')} />
                <Header label="Info" align="right" {...headerSort('infoFlags')} />
                <Header label="Warnings" align="right" {...headerSort('warningFlags')} />
                <Header label="Warnings / run" align="right" {...headerSort('warningFlagsPerRun')} />
                <Header label="Last run" align="right" {...headerSort('lastRunAt')} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((row) => <RankingRow key={row.expertID} row={row} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Header({ label, align = 'left', direction, onSort }: {
  label: string;
  align?: 'left' | 'right';
  direction?: SortDirection;
  onSort: () => void;
}) {
  const Icon = direction === 'ascending' ? ArrowUp : direction === 'descending' ? ArrowDown : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={direction ?? 'none'}
      className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={onSort}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded uppercase tracking-wide hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        {label}
        <Icon aria-hidden="true" className={`h-3.5 w-3.5 ${direction ? 'text-primary' : 'text-gray-400'}`} />
      </button>
    </th>
  );
}

function RankingRow({ row }: { row: AiDetectionMetricRow }) {
  return (
    <tr className={row.isTopWarningQuartile ? 'bg-amber-50/40' : undefined}>
      <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-gray-500">
        {row.warningRank ?? '—'}
      </td>
      <td className="max-w-[320px] px-4 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate font-medium text-gray-900">{row.name}</span>
          {row.isTopWarningQuartile && <AiDetectionWarningPill />}
        </div>
        <p className="truncate text-xs text-gray-500">{row.email || '(no email)'}</p>
        <p className="truncate font-mono text-[10px] text-gray-400">{row.expertID}</p>
      </td>
      <NumberCell value={row.detectionRuns} />
      <NumberCell value={row.infoFlags} />
      <NumberCell value={row.warningFlags} accent={row.warningFlags > 0 ? 'text-amber-700' : undefined} />
      <td className="px-4 py-3 text-right font-mono tabular-nums text-gray-700">
        {row.warningFlagsPerRun.toFixed(2)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-xs text-gray-500">
        {row.lastRunAt ? row.lastRunAt.toLocaleDateString() : '—'}
      </td>
    </tr>
  );
}

function NumberCell({ value, accent = 'text-gray-700' }: { value: number; accent?: string }) {
  return <td className={`px-4 py-3 text-right font-mono tabular-nums ${accent}`}>{value.toLocaleString()}</td>;
}
